"use client";

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listSellers, Seller } from "@/services/sellerService";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { columns } from "@/components/sellers/columns";

export default function SellersPage() {
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const navigate = useNavigate();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<any>({ total: 0, page: 1, pageSize: 10 });

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSellers({ page, page_size: pagination.pageSize });
      setSellers(res.data || []);
      setPagination(res.pagination || { total: 0, page: 1, pageSize: pagination.pageSize });
    } catch (err: any) {
      setError(err?.message || "Failed to load sellers");
    } finally {
      setLoading(false);
    }
  }, [page, pagination.pageSize]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const handleRowClick = (row: Seller) => {
    if (!storeType || !storeId) return;
    navigate(`/${storeType}/${storeId}/sellers/${row.id}`);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPage(newPageIndex + 1);
  };

  return (
    <div className="space-y-4">
      <TableContainer>
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={sellers}
            pagination={pagination}
            pageIndex={Math.max(0, page - 1)}
            onPageChange={handlePageChange}
            loading={loading}
            error={error}
            title="Sellers"
            description="List of sellers you are selling to"
            searchPlaceholder="Search sellers..."
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="customers"
            loadingMessage="Loading sellers..."
          />
        </div>
      </TableContainer>
    </div>
  );
}
